
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { Disc } from 'lucide-react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Nightlife from './components/Nightlife';
import Equipment from './components/Equipment';
import Gallery from './components/Gallery';
import MusicSection from './components/MusicSection';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import LegalNotice from './components/LegalNotice';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLegalOpen, setIsLegalOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative bg-dark min-h-screen text-white font-sans selection:bg-gold selection:text-dark">
      {/* Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gold z-[100] origin-left"
        style={{ scaleX }}
      />

      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] bg-dark flex flex-col items-center justify-center space-y-6"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative w-24 h-24"
            >
              <div className="absolute inset-0 border-2 border-gold/20 rounded-full animate-ping" />
              <div className="absolute inset-2 border-2 border-gold rounded-full animate-spin [animation-duration:3s]" />
              <div className="flex items-center justify-center h-full">
                <Disc className="w-10 h-10 text-gold" />
              </div>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-2xl font-serif tracking-[0.3em] text-gold uppercase"
            >
              DJ SAFF
            </motion.h1>
          </motion.div>
        )}
      </AnimatePresence>

      {!isLoading && (
        <>
          <Navbar />
          <main>
            <Hero />
            <About />
            <Services />
            {/*<Nightlife />*/}
            <Equipment />
            {/*<Gallery />*/}
            <MusicSection />
            <Testimonials />
            <Contact />
          </main>
          <Footer onOpenLegal={() => setIsLegalOpen(true)} />
          
          <LegalNotice isOpen={isLegalOpen} onClose={() => setIsLegalOpen(false)} />
        </>
      )}

      {/* Background Aurora Elements */}
      <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-gold/5 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-gold/5 rounded-full blur-[120px] animate-pulse [animation-delay:2s]" />
      </div>
    </div>
  );
};

export default App;
